/**
 * SecureEmbed AEMO Data Ingestion
 *
 * Redesigned to match DeploymentHub style:
 * - Environment selector in header with Region and Data Types
 * - Run/Stop buttons matching deployment controls (Step Functions has no native
 *   pause/resume — Stop halts the execution; there is no fake "pause" state)
 * - Live logs panel
 * - Ingestion history with delete buttons
 * - Pipeline progress visualization
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Play, Square, RefreshCw, Trash2, Calendar, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, AlertTriangle, Terminal, ArrowRight, Loader2,
  Database, Zap, Search, FileText, MapPin, Filter, ToggleLeft, ToggleRight
} from 'lucide-react';
import { ingestionApi, operationsApi, ENV_COLORS } from '../services/api';

type Env = 'dev' | 'test' | 'uat' | 'prod';

const ENVIRONMENTS: Env[] = ['dev', 'test', 'uat', 'prod'];

// NEM Regions
const NEM_REGIONS = [
  { id: 'all', name: 'All Regions' },
  { id: 'NSW1', name: 'New South Wales' },
  { id: 'VIC1', name: 'Victoria' },
  { id: 'QLD1', name: 'Queensland' },
  { id: 'SA1', name: 'South Australia' },
  { id: 'TAS1', name: 'Tasmania' },
];

// AEMO Data Types
const DATA_TYPES = [
  { id: 'all', name: 'All Types' },
  { id: 'dispatch', name: 'Dispatch' },
  { id: 'trading', name: 'Trading' },
  { id: 'predispatch', name: 'Pre-Dispatch' },
  { id: 'forecast', name: 'Forecast' },
];

// Pipeline stages
interface PipelineStage {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  icon: React.ElementType;
}

const PIPELINE_STAGES: PipelineStage[] = [
  { id: 'fetch', name: 'Fetch', description: 'Download from nemweb.com.au', status: 'pending', icon: Database },
  { id: 'transform', name: 'Transform', description: 'Parse CSV to JSON', status: 'pending', icon: FileText },
  { id: 'embed', name: 'Embed', description: 'Generate Titan embeddings', status: 'pending', icon: Zap },
  { id: 'index', name: 'Index', description: 'Store in OpenSearch', status: 'pending', icon: Search },
];

interface LogEntry {
  id?: string;
  timestampMs?: number;
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'success';
  message: string;
  stage?: string;
  source?: 'system' | 'run';
}

interface IngestionRun {
  executionId: string;
  status: string;
  startDate: string | null;
  stopDate: string | null;
  recordsProcessed?: number;
  embeddingsCreated?: number;
  indexedCount?: number;
  fileCount?: number;
  chunkSize?: number;
  chunkKeysTotal?: number;
  chunkKeysOffset?: number;
  chunkKeysOffsetNext?: number;
  parameters?: Record<string, any>;
  error?: string | null;
  cause?: string | null;
}

interface DeleteStatus {
  status: 'pending' | 'submitted' | 'completed' | 'failed' | 'skipped';
  deletedCount?: number;
  error?: string;
  task?: string;
  reason?: string;
}

// Format date for input field
const formatDateForInput = (date: Date): string => date.toISOString().split('T')[0];

// Get default date range (last 7 days)
const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return { start: formatDateForInput(start), end: formatDateForInput(end) };
};

const AemoIngestionPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  // Header controls
  const [env, setEnv] = useState<Env>('dev');
  const [region, setRegion] = useState('all');
  const [dataType, setDataType] = useState('all');
  const [updatedOnly, setUpdatedOnly] = useState(false);

  // Date range
  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [useCurrentEndDate, setUseCurrentEndDate] = useState(false);

  // State
  const [isRunning, setIsRunning] = useState(false);
  const [isDeployed, setIsDeployed] = useState(false);
  const [stages, setStages] = useState<PipelineStage[]>(PIPELINE_STAGES);
  const [systemLogs, setSystemLogs] = useState<LogEntry[]>([]);
  const [runLogs, setRunLogs] = useState<LogEntry[]>([]);
  const [history, setHistory] = useState<IngestionRun[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const [showFailedRuns, setShowFailedRuns] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [deletingRuns, setDeletingRuns] = useState<string[]>([]);
  const [deleteStatuses, setDeleteStatuses] = useState<Record<string, DeleteStatus>>({});
  const [selectedRuns, setSelectedRuns] = useState<string[]>([]);
  const [progressPercent, setProgressPercent] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [liveCounts, setLiveCounts] = useState<{
    records?: number | null;
    embeddings?: number | null;
    indexed?: number | null;
    files?: number | null;
    recordsTotal?: number | null;
    embeddingsTotal?: number | null;
    indexedTotal?: number | null;
    filesTotal?: number | null;
    chunkSize?: number | null;
    chunkKeysTotal?: number | null;
    chunkKeysOffset?: number | null;
    chunkKeysOffsetNext?: number | null;
  } | null>(null);
  const [lastStageError, setLastStageError] = useState('');
  const [lastStageCause, setLastStageCause] = useState('');
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);

  // Logs state
  const [logExecutionId, setLogExecutionId] = useState<string>('');

  // Schedule state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleInterval, setScheduleInterval] = useState('daily');
  const [scheduleUpdating, setScheduleUpdating] = useState(false);
  const [scheduleExpression, setScheduleExpression] = useState('');
  const [scheduleParams, setScheduleParams] = useState<Record<string, any>>({});
  const [scheduleLastRun, setScheduleLastRun] = useState<string | null>(null);
  const [scheduleNextRun, setScheduleNextRun] = useState<string | null>(null);
  const envColors = ENV_COLORS[env];

  const logsEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const logsPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastErrorRef = useRef<string>('');
  const lastDeploymentWarningRef = useRef<string>('');
  const lastStatusRef = useRef<string | null>(null);
  const lastLogKeyRef = useRef<string>('');
  const lastLogExecutionIdRef = useRef<string>('');
  const lastShowLogsRef = useRef<boolean>(false);
  const logNextTokenRef = useRef<string | null>(null);
  const logSeqRef = useRef<number>(0);
  const envRef = useRef<Env>(env);
  const historyKeyRef = useRef<string>('');
  const deleteInFlightRef = useRef(false);
  // H3: AbortController to cancel in-flight fetch requests on env change / unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  const combinedLogs = useMemo(() => {
    const combined = [...systemLogs, ...runLogs];
    return combined
      .map((log, index) => ({ log, index }))
      .sort((a, b) => {
        const aTime = a.log.timestampMs ?? Date.parse(a.log.timestamp) ?? 0;
        const bTime = b.log.timestampMs ?? Date.parse(b.log.timestamp) ?? 0;
        if (aTime !== bTime) return aTime - bTime;
        return a.index - b.index;
      })
      .map(({ log }) => log);
  }, [systemLogs, runLogs]);

  const currentEndDate = formatDateForInput(new Date());
  const displayEndDate = useCurrentEndDate ? currentEndDate : endDate;

  const ingestedRanges = useMemo(() => {
    const ranges: Array<{ startMs: number; endMs: number }> = [];
    const nowKey = new Date().toISOString().slice(0, 10);
    const toDayMs = (dateKey: string): number | null => {
      const parsed = new Date(`${dateKey}T00:00:00Z`);
      return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
    };

    history
      .filter((run) => run.status === 'SUCCEEDED')
      .forEach((run) => {
        const params = run.parameters || {};
        let start = params.start_date || '';
        let end = params.end_date || '';
        if (params.end_date_current) {
          end = nowKey;
        }
        if (!start && end) start = end;
        if (!end && start) end = start;
        if (!start || !end) return;
        const startMs = toDayMs(start);
        const endMs = toDayMs(end);
        if (startMs === null || endMs === null) return;
        const normalized = startMs <= endMs
          ? { startMs, endMs }
          : { startMs: endMs, endMs: startMs };
        ranges.push(normalized);
      });

    ranges.sort((a, b) => (a.startMs - b.startMs) || (a.endMs - b.endMs));

    const merged: Array<{ startMs: number; endMs: number }> = [];
    const oneDayMs = 24 * 60 * 60 * 1000;
    ranges.forEach((range) => {
      const last = merged[merged.length - 1];
      if (!last) {
        merged.push({ ...range });
        return;
      }
      if (range.startMs <= last.endMs + oneDayMs) {
        last.endMs = Math.max(last.endMs, range.endMs);
        return;
      }
      merged.push({ ...range });
    });

    const formatRangeDate = (dateMs: number): string => {
      const date = new Date(dateMs);
      const dd = String(date.getUTCDate()).padStart(2, '0');
      const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
      const yy = String(date.getUTCFullYear()).slice(-2);
      return `${dd}/${mm}/${yy} 24:00`;
    };

    return merged.map((range) => `${formatRangeDate(range.startMs)} → ${formatRangeDate(range.endMs)}`);
  }, [history]);

  const formatDuration = (seconds: number | null): string => {
    if (seconds === null || seconds < 0) return '-';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const hh = String(hrs).padStart(2, '0');
    const mm = String(mins).padStart(2, '0');
    const ss = String(secs).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  const formatCountWithTotal = (value?: number | null, total?: number | null): string => {
    if (typeof value !== 'number') return '—';
    if (typeof total !== 'number') return value.toLocaleString();
    return `${value.toLocaleString()}/${total.toLocaleString()}`;
  };

  // Auto-scroll logs
  useEffect(() => {
    const lastLog = combinedLogs[combinedLogs.length - 1];
    const lastKey = lastLog?.id || (lastLog ? `${lastLog.timestamp}|${lastLog.message}` : '');
    if (!logsEndRef.current) {
      lastLogKeyRef.current = lastKey;
      return;
    }
    if (!autoScroll) {
      lastLogKeyRef.current = lastKey;
      return;
    }
    if (lastKey && lastKey !== lastLogKeyRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
    lastLogKeyRef.current = lastKey;
  }, [combinedLogs, autoScroll]);

  // Add log entry
  const addLog = useCallback((level: LogEntry['level'], message: string, stage?: string) => {
    const now = Date.now();
    logSeqRef.current += 1;
    setSystemLogs(prev => [...prev.slice(-200), {
      id: `system-${now}-${logSeqRef.current}`,
      timestampMs: now,
      timestamp: new Date(now).toISOString(),
      level,
      message,
      stage,
      source: 'system'
    }]);
  }, []);

  const formatDateTime = (value?: string | null) => {
    if (!value || value === 'Never') return value || '-';
    const dt = new Date(value);
    return Number.isNaN(dt.getTime()) ? value : dt.toLocaleString();
  };

  const formatLogMessage = useCallback((message: string): string => {
    const trimmed = message.trim();
    if (!(trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      return message;
    }
    try {
      const parsed = JSON.parse(trimmed);
      const type = String(parsed.type || '');
      const name = parsed.details?.name;
      const inputRaw = parsed.details?.input;
      const outputRaw = parsed.details?.output;

      const parseJson = (value?: string) => {
        if (!value || typeof value !== 'string') return null;
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }
      };

      const truncate = (value: string, max = 160) => (
        value.length > max ? `${value.slice(0, max)}...` : value
      );

      const input = parseJson(inputRaw);
      const output = parseJson(outputRaw);

      if (type === 'ExecutionStarted') {
        const source = input?.source ? `source=${input.source}` : 'source=unknown';
        const options = input?.options || {};
        const region = options.region || options.regions || 'All Regions';
        const dataType = options.data_type || options.data_types || 'All Types';
        const range = options.start_date || options.end_date
          ? `range=${options.start_date || '—'}→${options.end_date || '—'}`
          : 'range=default';
        return `Execution started (${source}, region=${region}, type=${dataType}, ${range})`;
      }

      if (type === 'TaskStateEntered') {
        return `Entered ${name || 'task'} step`;
      }

      if (type === 'TaskStateExited') {
        return '';
      }

      if (type === 'TaskScheduled') {
        return `Scheduled ${name || 'task'} step`;
      }

      if (type === 'TaskStarted') {
        return `Started ${name || 'task'} step`;
      }

      if (type === 'TaskSucceeded') {
        const payload = output?.Payload || output?.fetch_result?.Payload || output?.transform_result?.Payload;
        const status = payload?.status || output?.status || 'SUCCESS';
        const files = payload?.file_count ?? payload?.files_downloaded?.length;
        const bytes = payload?.total_bytes;
        const fileInfo = typeof files === 'number' ? `, files=${files}` : '';
        const byteInfo = typeof bytes === 'number' ? `, bytes=${bytes}` : '';
        return `Task succeeded: ${name || 'task'} (status=${status}${fileInfo}${byteInfo})`;
      }

      if (type === 'TaskFailed') {
        const error = parsed.details?.error || 'TaskFailed';
        const cause = parsed.details?.cause || '';
        return `Task failed: ${name || 'task'} (${error}${cause ? `: ${truncate(String(cause))}` : ''})`;
      }

      if (type === 'ChoiceStateEntered' || type === 'ChoiceStateExited') {
        return type === 'ChoiceStateEntered' ? `Evaluated ${name || 'choice'} condition` : '';
      }

      if (type === 'SucceedStateEntered' || type === 'SucceedStateExited') {
        return type === 'SucceedStateEntered' ? `${name || 'Succeed'} state completed` : '';
      }

      if (type === 'FailStateEntered') {
        return `${name || 'Fail'} state entered`;
      }

      if (type === 'ExecutionSucceeded') {
        return 'Execution succeeded';
      }

      if (type === 'ExecutionFailed') {
        const error = parsed.details?.error || 'ExecutionFailed';
        const cause = parsed.details?.cause || '';
        return `Execution failed (${error}${cause ? `: ${truncate(String(cause))}` : ''})`;
      }
      if (type) {
        return `Step Functions event: ${type}`;
      }
    } catch {
      return 'Step Functions event';
    }
    return 'Step Functions event';
  }, []);

  const buildHistoryKey = (items: IngestionRun[]) => (
    items.map((run) => [
      run.executionId,
      run.status,
      run.startDate,
      run.stopDate,
      run.error,
      run.cause
    ].join('|')).join('||')
  );

  const describeParameters = (params?: Record<string, any>) => {
    if (!params || Object.keys(params).length === 0) {
      return ['All Regions', 'All Types'];
    }
    const lines: string[] = [];
    if (params.start_date || params.end_date || params.end_date_current) {
      const endLabel = params.end_date_current
        ? 'Today'
        : (params.end_date || (params.start_date ? 'Today' : '—'));
      lines.push(`Date: ${params.start_date || '—'} → ${endLabel}`);
    }
    const regionValue = params.region || params.regions;
    lines.push(`Region: ${regionValue || 'All Regions'}`);
    const dataTypeValue = params.data_type || params.data_types;
    if (Array.isArray(dataTypeValue)) {
      lines.push(`Type: ${dataTypeValue.join(', ') || 'All Types'}`);
    } else {
      lines.push(`Type: ${dataTypeValue || 'All Types'}`);
    }
    if (params.updated_only) {
      lines.push('Mode: Updated only');
    }
    return lines;
  };

  const getScheduleTimeLabel = (expression: string) => {
    if (!expression) return '—';
    const expr = expression.toLowerCase();
    if (expr.startsWith('rate(')) {
      return expression.replace('rate(', 'Every ').replace(')', '');
    }
    if (expr.startsWith('cron(')) {
      const cron = expr.replace('cron(', '').replace(')', '').split(' ');
      if (cron.length >= 2 && cron[0] && cron[1]) {
        const minute = cron[0].padStart(2, '0');
        const hour = cron[1].padStart(2, '0');
        return `${hour}:${minute} UTC`;
      }
    }
    return expression;
  };

  const logLevelFromMessage = (message: string): LogEntry['level'] => {
    const trimmed = message.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        const type = String(parsed.type || parsed.details?.name || '');
        if (/Failed|ExecutionFailed|FailStateEntered|TaskFailed/i.test(type)) {
          return 'error';
        }
        if (/TimedOut|Aborted/i.test(type)) {
          return 'warn';
        }
        return 'info';
      } catch {
        // Fall through to heuristics for non-JSON strings.
      }
    }
    if (/\b(success|succeeded)\b/i.test(message)) return 'success';
    if (/\b(warn|warning)\b/i.test(message)) return 'warn';
    if (/\b(error|failed|exception)\b/i.test(message)) return 'error';
    return 'info';
  };

  const logRunOptions = useMemo(() => {
    const options: { id: string; label: string }[] = [];
    if (currentExecutionId) {
      const currentRun = history.find(run => run.executionId === currentExecutionId);
      const label = currentRun?.startDate
        ? `${formatDateTime(currentRun.startDate)}${isRunning ? ' (current)' : ''}`
        : `${currentExecutionId}${isRunning ? ' (current)' : ''}`;
      options.push({ id: currentExecutionId, label });
    }
    history.forEach(run => {
      if (!options.find(option => option.id === run.executionId)) {
        const label = run.startDate ? formatDateTime(run.startDate) : run.executionId;
        options.push({ id: run.executionId, label });
      }
    });
    return options;
  }, [history, currentExecutionId, isRunning]);

  // Check if AEMO infrastructure is deployed (layer 07a-aemo)
  const checkDeployment = useCallback(async () => {
    try {
      // First check if the AEMO state machine exists via ingestion API
      const ingestionStatus = await ingestionApi.getStatus({ env });

      const deploymentNote = ingestionStatus?.ingestion?.note || '';
      const notDeployed = /not deployed/i.test(deploymentNote) || /StateMachine/i.test(deploymentNote);
      // If there's a note about state machine not deployed, infrastructure is not ready
      if (notDeployed) {
        setIsDeployed(false);
        addLog('warn', 'AEMO pipeline not deployed. Go to Data Stores → AEMO Datastore to deploy.');
        return;
      }

      // Also check the layer status from operations API
      const status = await operationsApi.getStatus();
      const envStatus = status?.environments?.[env];
      const layer07aDeployed = envStatus?.layers?.['07a-aemo']?.deployed;

      // Consider deployed if either the state machine works OR layer shows deployed
      const deployed = !notDeployed || !!layer07aDeployed;
      setIsDeployed(deployed);

      if (!deployed) {
        addLog('warn', 'AEMO infrastructure not deployed. Go to Data Stores → AEMO Datastore to deploy.');
      }
    } catch (e) {
      // If we can't check, assume not deployed
      setIsDeployed(false);
      const warning = 'Could not verify deployment status.';
      if (lastDeploymentWarningRef.current !== warning) {
        lastDeploymentWarningRef.current = warning;
        addLog('warn', warning);
      }
    }
  }, [env, addLog, logExecutionId]);

  // Fetch ingestion status
  const fetchStatus = useCallback(async () => {
    // H3: Abort any in-flight request before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestEnv = env;
    try {
      const resp = await ingestionApi.getStatus({ env, include_failed: showFailedRuns });
      if (controller.signal.aborted || envRef.current !== requestEnv) {
        return;
      }

      if (resp?.ingestion) {
        const ing = resp.ingestion;

        // Update history
        const rawHistory = Array.isArray(ing.history) ? ing.history : [];
        const baseHistory = rawHistory.length ? rawHistory : (ing.currentExecution ? [ing.currentExecution] : []);
        if (baseHistory.length) {
          const mappedHistory: IngestionRun[] = baseHistory.map((h: any): IngestionRun => ({
            executionId: h.executionId || h.name,
            status: h.status,
            startDate: h.startDate,
            stopDate: h.stopDate,
            recordsProcessed: h.recordsProcessed,
            embeddingsCreated: h.embeddingsCreated,
            indexedCount: h.indexedCount,
            fileCount: h.fileCount,
            chunkSize: h.chunkSize,
            chunkKeysTotal: h.chunkKeysTotal,
            chunkKeysOffset: h.chunkKeysOffset,
            chunkKeysOffsetNext: h.chunkKeysOffsetNext,
            parameters: h.parameters,
            error: h.error || null,
            cause: h.cause || null
          }));
          const nextKey = buildHistoryKey(mappedHistory);
          if (nextKey !== historyKeyRef.current) {
            historyKeyRef.current = nextKey;
            setHistory(mappedHistory);
          }
          setSelectedRuns(prev => prev.filter(id => mappedHistory.some((run: IngestionRun) => run.executionId === id)));
          const currentId = ing.currentExecution?.executionId || ing.currentExecution?.name || mappedHistory[0]?.executionId;
          setCurrentExecutionId(currentId || null);
          const currentExecution = ing.currentExecution || mappedHistory.find(run => run.executionId === currentId);
          const recordsTotal = currentExecution?.recordsProcessed ?? null;
          const filesTotal = currentExecution?.fileCount ?? null;
          const chunkSize = currentExecution?.chunkSize ?? 100;
          const chunkKeysTotal = currentExecution?.chunkKeysTotal ?? null;
          const chunkOffset =
            currentExecution?.chunkKeysOffsetNext ??
            currentExecution?.chunkKeysOffset ??
            null;
          const embeddingsTotal = recordsTotal;
          const embeddingsDone =
            chunkKeysTotal && chunkOffset !== null
              ? Math.min(embeddingsTotal ?? 0, chunkOffset * chunkSize)
              : currentExecution?.embeddingsCreated ?? null;
          const indexedTotal = recordsTotal;
          const indexedDone = embeddingsDone ?? currentExecution?.indexedCount ?? null;
          setLiveCounts({
            records: currentExecution?.recordsProcessed ?? null,
            embeddings: embeddingsDone,
            indexed: indexedDone,
            files: currentExecution?.fileCount ?? null,
            recordsTotal,
            embeddingsTotal,
            indexedTotal,
            filesTotal,
            chunkSize,
            chunkKeysTotal,
            chunkKeysOffset: currentExecution?.chunkKeysOffset ?? null,
            chunkKeysOffsetNext: currentExecution?.chunkKeysOffsetNext ?? null,
          });
          const hasLogId = logExecutionId && mappedHistory.some(run => run.executionId === logExecutionId);
          if (logExecutionId && !hasLogId) {
            const fallbackId = currentId || '';
            setLogExecutionId(fallbackId);
            logNextTokenRef.current = null;
            setRunLogs([]);
          }
          if (!logExecutionId && currentId) {
            setLogExecutionId(currentId);
            setRunLogs([]);
            logNextTokenRef.current = null;
          }
        } else {
          historyKeyRef.current = '';
          setHistory([]);
          setSelectedRuns([]);
          setCurrentExecutionId(null);
          setLiveCounts({
            records: null,
            embeddings: null,
            indexed: null,
            files: null,
            recordsTotal: null,
            embeddingsTotal: null,
            indexedTotal: null,
            filesTotal: null,
            chunkSize: 100,
            chunkKeysTotal: null,
            chunkKeysOffset: null,
            chunkKeysOffsetNext: null,
          });
          setLogExecutionId('');
          setRunLogs([]);
          logNextTokenRef.current = null;
        }

        // Check if running
        const currentStatus = ing.currentExecution?.status || null;
        const nextRunning = currentStatus === 'RUNNING';
        if (nextRunning !== isRunning) {
          setIsRunning(nextRunning);
        }
        if (nextRunning && lastStatusRef.current !== 'RUNNING') {
          addLog('info', `Ingestion in progress: ${ing.currentExecution.executionId}`);
        }
        lastStatusRef.current = currentStatus;

        // Progress details
        const progress = nextRunning ? ing.currentExecution?.progress : null;
        if (progress) {
          setProgressPercent(typeof progress.percentComplete === 'number' ? progress.percentComplete : null);
          setElapsedSeconds(typeof progress.elapsedSeconds === 'number' ? progress.elapsedSeconds : null);
          setRemainingSeconds(
            typeof progress.estimatedRemainingSeconds === 'number'
              ? progress.estimatedRemainingSeconds
              : null
          );
          if (progress.stageStatus) {
            setStages(prev => prev.map((stage) => ({
              ...stage,
              status: progress.stageStatus[stage.name] || stage.status
            })));
          }
          const err = progress.lastError || '';
          const cause = progress.lastCause || '';
          setLastStageError(err);
          setLastStageCause(cause);
          if (err && err !== lastErrorRef.current) {
            lastErrorRef.current = err;
            addLog('error', `Pipeline error: ${err}${cause ? ` (${cause})` : ''}`);
            if (ing.currentExecution?.status === 'FAILED') {
              setError(`${err}${cause ? `: ${cause}` : ''}`);
            }
          }
        } else {
          setProgressPercent(null);
          setElapsedSeconds(null);
          setRemainingSeconds(null);
          setLastStageError('');
          setLastStageCause('');
          setStages(PIPELINE_STAGES);
        }

        // Update schedule status
        if (ing.scheduleEnabled !== undefined) {
          setScheduleEnabled(ing.scheduleEnabled);
        }
        if (ing.scheduleExpression) {
          setScheduleExpression(ing.scheduleExpression);
          // Parse schedule expression to interval
          const expr = ing.scheduleExpression.toLowerCase();
          if (expr.includes('hour')) setScheduleInterval('hourly');
          else if (expr.includes('day') || expr.includes('0 0')) setScheduleInterval('daily');
          else if (expr.includes('week') || expr.includes('MON')) setScheduleInterval('weekly');
          else if (expr.includes('month')) setScheduleInterval('monthly');
        }
        if (ing.scheduleParams) {
          setScheduleParams(ing.scheduleParams);
        }
        if (ing.scheduleLastRun !== undefined) {
          setScheduleLastRun(ing.scheduleLastRun || null);
        }
        if (ing.scheduleNextRun !== undefined) {
          setScheduleNextRun(ing.scheduleNextRun || null);
        }
      }
    } catch (e: any) {
      if (e?.response?.status === 404 || e?.message?.includes('not found')) {
        addLog('warn', 'Ingestion pipeline not deployed. Go to Data Stores → AEMO Datastore to deploy.');
        setIsDeployed(false);
      }
    }
  }, [env, addLog, showFailedRuns]);

  // Update schedule
  const updateSchedule = async (enabled: boolean, interval: string) => {
    setScheduleUpdating(true);
    try {
      // Convert interval to cron expression
      const scheduleExpressions: Record<string, string> = {
        'hourly': 'rate(1 hour)',
        'daily': 'cron(0 0 * * ? *)',      // Midnight UTC
        'weekly': 'cron(0 0 ? * MON *)',   // Monday midnight UTC
        'monthly': 'cron(0 0 1 * ? *)',    // 1st of month midnight UTC
        '6-hourly': 'rate(6 hours)',
        '12-hourly': 'rate(12 hours)'
      };

      const effectiveEndDate = useCurrentEndDate ? undefined : endDate;
      await ingestionApi.updateSchedule({
        env,
        enabled,
        scheduleExpression: scheduleExpressions[interval] || scheduleExpressions['daily'],
        region: region !== 'all' ? region : undefined,
        dataTypes: dataType !== 'all' ? [dataType] : undefined,
        updatedOnly,
        startDate,
        endDate: effectiveEndDate,
        endDateCurrent: useCurrentEndDate || undefined
      });

      setScheduleEnabled(enabled);
      setScheduleInterval(interval);
      setScheduleExpression(scheduleExpressions[interval] || scheduleExpressions['daily']);
      setScheduleParams({
        region: region !== 'all' ? region : undefined,
        data_types: dataType !== 'all' ? [dataType] : undefined,
        updated_only: updatedOnly,
        start_date: startDate,
        end_date: effectiveEndDate,
        end_date_current: useCurrentEndDate || undefined
      });
      addLog('success', `Schedule ${enabled ? 'enabled' : 'disabled'}: ${interval}`);
      setMessage(`Schedule ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (e: any) {
      addLog('error', `Failed to update schedule: ${e.message}`);
      setError(`Failed to update schedule: ${e.message}`);
    } finally {
      setScheduleUpdating(false);
    }
  };

  const fetchExecutionLogs = useCallback(async (reset: boolean) => {
    if (!logExecutionId) return;
    try {
      const resp = await ingestionApi.getLogs({
        env,
        executionName: logExecutionId,
        nextToken: reset ? undefined : (logNextTokenRef.current ?? undefined),
        limit: 200
      });

      const events = Array.isArray(resp?.events) ? resp.events : [];
      const mapped: LogEntry[] = events.map((evt: any, idx: number) => {
        const timestampMs = typeof evt.timestamp === 'number' ? evt.timestamp : Date.now();
        const message = String(evt.message || '').trim();
        const id =
          evt.eventId ||
          `${evt.logStreamName || 'log'}-${timestampMs}-${idx}`;
        const formatted = formatLogMessage(message);
        if (!formatted) {
          return null as unknown as LogEntry;
        }
        return {
          id,
          timestampMs,
          timestamp: new Date(timestampMs).toISOString(),
          level: logLevelFromMessage(message),
          message: formatted,
          source: 'run'
        };
      }).filter(Boolean);

      setRunLogs(prev => {
        const next = reset ? [] : prev.slice();
        const existing = new Set(next.map(item => `${item.timestampMs || 0}|${item.message}`));
        let added = false;
        mapped.forEach((entry: LogEntry) => {
          const key = `${entry.timestampMs || 0}|${entry.message}`;
          if (!existing.has(key)) {
            next.push(entry);
            existing.add(key);
            added = true;
          }
        });
        if (!reset && !added) {
          return prev;
        }
        return next.slice(-500);
      });
      if (resp?.nextToken) {
        logNextTokenRef.current = resp.nextToken;
      } else if (reset) {
        logNextTokenRef.current = null;
      }
    } catch (e: any) {
      addLog('error', `Failed to fetch logs: ${e?.message || 'Unknown error'}`);
    }
  }, [env, logExecutionId, addLog, logLevelFromMessage, formatLogMessage]);

  // Initial load and polling
  useEffect(() => {
    // H3: Abort in-flight requests when environment changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    envRef.current = env;
    setLogExecutionId('');
    setRunLogs([]);
    logNextTokenRef.current = null;
    setSelectedRuns([]);
    setHistory([]);
    setDeleteStatuses({});
    setCurrentExecutionId(null);
    setLiveCounts({
      records: null,
      embeddings: null,
      indexed: null,
      files: null,
      recordsTotal: null,
      embeddingsTotal: null,
      indexedTotal: null,
      filesTotal: null,
      chunkSize: 100,
      chunkKeysTotal: null,
      chunkKeysOffset: null,
      chunkKeysOffsetNext: null,
    });
    historyKeyRef.current = '';
    checkDeployment();
    fetchStatus();
  }, [env]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (isRunning) {
      pollRef.current = setInterval(fetchStatus, 3000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isRunning, fetchStatus]);

  useEffect(() => {
    fetchStatus();
  }, [showFailedRuns, fetchStatus]);

  useEffect(() => {
    if (logsPollRef.current) {
      clearInterval(logsPollRef.current);
      logsPollRef.current = null;
    }
    if (!showLogs || !logExecutionId) {
      lastShowLogsRef.current = showLogs;
      lastLogExecutionIdRef.current = logExecutionId;
      return;
    }
    const shouldReset =
      logExecutionId !== lastLogExecutionIdRef.current ||
      (!lastShowLogsRef.current && showLogs);
    if (shouldReset) {
      setRunLogs([]);
      logNextTokenRef.current = null;
      fetchExecutionLogs(true);
    }
    lastShowLogsRef.current = showLogs;
    lastLogExecutionIdRef.current = logExecutionId;
  }, [showLogs, logExecutionId, fetchExecutionLogs]);

  // Run ingestion
  const handleRun = async () => {
    if (!isDeployed) {
      setError('Infrastructure not deployed. Go to Data Stores → AEMO Datastore to deploy first.');
      return;
    }

    setBusy(true);
    setError('');
    setMessage('');
    setSystemLogs([]);
    setRunLogs([]);
    logNextTokenRef.current = null;
    setStages(PIPELINE_STAGES);

    addLog('info', `Starting AEMO ingestion for ${env.toUpperCase()}`);
    const endLabel = useCurrentEndDate ? 'Today' : endDate;
    addLog('info', `Date range: ${startDate} to ${endLabel}`);
    addLog('info', `Region: ${region}, Data Type: ${dataType}`);
    if (updatedOnly) {
      addLog('info', 'Mode: Updated/new records only');
    }

    try {
      const effectiveEndDate = useCurrentEndDate ? undefined : endDate;
      const resp = await ingestionApi.run({
        env,
        options: {
          start_date: startDate,
          end_date: effectiveEndDate,
          end_date_current: useCurrentEndDate || undefined,
          region: region !== 'all' ? region : undefined,
          data_type: dataType !== 'all' ? dataType : undefined,
          updated_only: updatedOnly
        }
      } as any);

      if (resp?.status === 'started') {
        setIsRunning(true);
        setMessage(`Ingestion started: ${resp.executionId}`);
        addLog('success', `Execution started: ${resp.executionId}`);
        if (resp.executionId) {
          setLogExecutionId(resp.executionId);
        }

        // Update first stage
        setStages(prev => prev.map((s, i) => ({
          ...s,
          status: i === 0 ? 'running' : 'pending'
        })));
      } else if (resp?.error) {
        throw new Error(resp.error);
      }
    } catch (e: any) {
      const errMsg = e?.response?.data?.error || e?.message || 'Failed to start ingestion';
      setError(errMsg);
      addLog('error', errMsg);

      if (errMsg.includes('not deployed') || errMsg.includes('StateMachineDoesNotExist')) {
        addLog('warn', 'Go to Data Stores → AEMO Datastore to deploy infrastructure.');
        setIsDeployed(false);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleRunNow = async () => {
    if (!isDeployed) {
      setError('Infrastructure not deployed. Go to Data Stores → AEMO Datastore to deploy first.');
      return;
    }

    const paramSource = Object.keys(scheduleParams || {}).length ? scheduleParams : {
      region: region !== 'all' ? region : undefined,
      data_types: dataType !== 'all' ? [dataType] : undefined,
      updated_only: updatedOnly,
      start_date: startDate,
      end_date: useCurrentEndDate ? undefined : endDate,
      end_date_current: useCurrentEndDate || undefined
    };

    const options = {
      start_date: paramSource.start_date,
      end_date: paramSource.end_date,
      end_date_current: paramSource.end_date_current,
      region: paramSource.region,
      data_type: Array.isArray(paramSource.data_types) ? paramSource.data_types[0] : paramSource.data_type,
      updated_only: paramSource.updated_only
    };

    setBusy(true);
    setError('');
    setMessage('');

    addLog('info', 'Running scheduled ingestion now...');

    try {
      const resp = await ingestionApi.run({ env, options } as any);
      if (resp?.status === 'started') {
        setIsRunning(true);
        setMessage(`Ingestion started: ${resp.executionId}`);
        addLog('success', `Execution started: ${resp.executionId}`);
        if (resp.executionId) {
          setLogExecutionId(resp.executionId);
        }
      } else if (resp?.error) {
        throw new Error(resp.error);
      }
    } catch (e: any) {
      const errMsg = e?.response?.data?.error || e?.message || 'Failed to start ingestion';
      setError(errMsg);
      addLog('error', errMsg);
    } finally {
      setBusy(false);
    }
  };

  // Stop ingestion
  const handleStop = async () => {
    setBusy(true);
    addLog('warn', 'Stopping ingestion...');

    try {
      await ingestionApi.stop({ env });
      setIsRunning(false);
      setMessage('Ingestion stopped');
      addLog('info', 'Ingestion stopped by user');
      setStages(PIPELINE_STAGES);
    } catch (e: any) {
      setError(e?.message || 'Failed to stop');
      addLog('error', 'Failed to stop ingestion');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteRuns = async (executionIds: string[]) => {
    if (executionIds.length === 0) return;
    if (deleteInFlightRef.current) return;
    if (!confirm(`Delete all data from ${executionIds.length} ingestion run(s)? This cannot be undone.`)) {
      return;
    }

    deleteInFlightRef.current = true;
    setDeletingRuns(executionIds);
    setDeleteStatuses(prev => {
      const next = { ...prev };
      executionIds.forEach(id => {
        next[id] = { status: 'pending' };
      });
      return next;
    });
    addLog('warn', `Deleting data for ${executionIds.length} run(s)...`);

    try {
      const resp = await ingestionApi.deleteData({ env, executionNames: executionIds });

      if (resp?.status === 'deleted') {
        const results = Array.isArray(resp.results) ? resp.results : [];
        const deletedTotal = results.reduce((sum: number, item: any) => sum + (item.deletedCount || 0), 0);
        const failed = results.filter((item: any) => item.error || item.reason);
        setDeleteStatuses(prev => {
          const next = { ...prev };
          results.forEach((item: any) => {
            const status: DeleteStatus['status'] = item.error
              ? 'failed'
              : item.reason
                ? 'skipped'
                : item.deletedCount > 0
                  ? 'completed'
                  : 'submitted';
            next[item.executionName] = {
              status,
              deletedCount: item.deletedCount,
              error: item.error,
              reason: item.reason,
              task: item.task,
            };
          });
          return next;
        });
        if (failed.length > 0) {
          setMessage(`Deleted ${deletedTotal} embeddings; failed to delete ${failed.length} run(s)`);
          const detail = failed
            .map((item: any) => `${item.executionName || 'run'}: ${item.error || item.reason || 'unknown error'}`)
            .join('; ');
          addLog('error', `Failed to delete ${failed.length} run(s): ${detail}`);
        } else {
          setMessage(`Deleted ${deletedTotal} embeddings from ${executionIds.length} run(s)`);
          addLog('success', `Deleted ${deletedTotal} embeddings`);
        }
        setSelectedRuns(prev => prev.filter(id => !executionIds.includes(id)));
        await fetchStatus();
      } else if (resp?.error) {
        throw new Error(resp.error);
      }
    } catch (e: any) {
      const errMsg = e?.response?.data?.error || e?.message || 'Delete failed';
      setError(errMsg);
      addLog('error', `Delete failed: ${errMsg}`);
      setDeleteStatuses(prev => {
        const next = { ...prev };
        executionIds.forEach(id => {
          next[id] = { status: 'failed', error: errMsg };
        });
        return next;
      });
    } finally {
      setDeletingRuns([]);
      deleteInFlightRef.current = false;
    }
  };

  const handleDeleteRun = async (executionId: string) => {
    await handleDeleteRuns([executionId]);
  };

  const handleRefreshLogs = async () => {
    if (!logExecutionId) return;
    setRunLogs([]);
    logNextTokenRef.current = null;
    await fetchExecutionLogs(true);
  };

  // Render stage status icon
  const StageIcon = ({ status }: { status: string }) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'running': return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Environment, Region, Data Type */}
      <div
        className="rounded-xl p-6 text-white"
        style={{
          background: `linear-gradient(135deg, ${envColors.primary} 0%, ${envColors.text} 100%)`,
        }}
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {!embedded && (
            <div>
              <h1 className="text-2xl font-bold">AEMO Data Ingestion</h1>
              <p className="text-purple-200 mt-1">Import Australian Energy Market data into SecureEmbed</p>
              <div className="mt-2 text-xs text-purple-100">
                <div className="font-semibold">Ingested ranges</div>
                {ingestedRanges.length === 0 ? (
                  <div>None yet.</div>
                ) : (
                  <div className="break-words">
                    {ingestedRanges.join(', ')}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Header Controls */}
          <div className="flex items-center gap-2 flex-nowrap">
            {/* Environment */}
            <div
              className="flex items-center gap-1 rounded-lg px-2 py-1.5 border"
              style={{ backgroundColor: envColors.bg, borderColor: envColors.primary }}
            >
              <span className="text-xs" style={{ color: envColors.text }}>Env:</span>
              <select
                value={env}
                onChange={(e) => setEnv(e.target.value as Env)}
                className="bg-transparent text-sm font-semibold focus:outline-none cursor-pointer"
                style={{ color: envColors.text }}
              >
                {ENVIRONMENTS.map(e => (
                  <option key={e} value={e} className="text-gray-900">{e.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Card */}
      <div
        className="rounded-xl p-6 shadow-sm border-2 space-y-4"
        style={{ backgroundColor: envColors.bg, borderColor: envColors.primary }}
      >
        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleRun}
            disabled={busy || isRunning || !isDeployed}
            className="px-6 py-2.5 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium transition-colors"
            style={{ backgroundColor: envColors.primary }}
          >
            <Play className="w-5 h-5" />
            Run Ingestion
          </button>

          <button
            onClick={handleStop}
            disabled={!isRunning || busy}
            className={`px-4 py-2.5 rounded-lg flex items-center gap-2 font-medium transition-colors ${
              isRunning
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Square className="w-5 h-5" />
            Stop
          </button>

          <button
            onClick={fetchStatus}
            disabled={busy}
            className="px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-gray-700"
          >
            <RefreshCw className={`w-5 h-5 ${busy ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="space-y-3">
          {/* Ingestion Parameters */}
          <div className="space-y-3">
            <div className="text-sm font-semibold text-gray-700">Ingestion Parameters</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  End Date
                </label>
                <input
                  type="date"
                  value={displayEndDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={useCurrentEndDate}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                />
                <label className="mt-2 inline-flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={useCurrentEndDate}
                    onChange={(e) => setUseCurrentEndDate(e.target.checked)}
                    className="rounded"
                  />
                  Use current date at run time
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Region
                </label>
                <select
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {NEM_REGIONS.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Filter className="w-4 h-4 inline mr-1" />
                  Data Type
                </label>
                <select
                  value={dataType}
                  onChange={(e) => setDataType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {DATA_TYPES.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {message}
          </div>
        )}
        {Object.keys(deleteStatuses).length > 0 && (
          <div className="mt-4 p-4 bg-gray-100 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
              Delete Progress
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
              {(() => {
                const values = Object.values(deleteStatuses);
                const counts = {
                  pending: values.filter(v => v.status === 'pending').length,
                  submitted: values.filter(v => v.status === 'submitted').length,
                  completed: values.filter(v => v.status === 'completed').length,
                  failed: values.filter(v => v.status === 'failed' || v.status === 'skipped').length,
                };
                return [
                  { label: 'Pending', value: counts.pending },
                  { label: 'Submitted', value: counts.submitted },
                  { label: 'Completed', value: counts.completed },
                  { label: 'Failed/Skipped', value: counts.failed },
                ];
              })().map(item => (
                <div key={item.label} className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="text-xs text-gray-500">{item.label}</div>
                  <div className="font-semibold text-gray-800">{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}
      </div>

      {/* Pipeline Progress */}
      {isRunning && (
        <div className="rounded-xl p-6 shadow-sm border-2" style={{ backgroundColor: envColors.bg, borderColor: envColors.primary }}>
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            Pipeline Progress
          </h3>
          {progressPercent !== null && (
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-4">
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-gray-500">Progress</span>
                <div className="font-semibold text-gray-800">{progressPercent}%</div>
              </div>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-gray-500">Ingestion started</span>
                <div className="font-semibold text-gray-800">
                  {(() => {
                    const activeStart = history.find(run => run.status === 'RUNNING')?.startDate
                      || history.find(run => run.executionId === currentExecutionId)?.startDate
                      || null;
                    return activeStart ? formatDateTime(activeStart) : '—';
                  })()}
                </div>
              </div>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-gray-500">Elapsed</span>
                <div className="font-semibold text-gray-800">{formatDuration(elapsedSeconds)}</div>
              </div>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-gray-500">Est. remaining</span>
                <div className="font-semibold text-gray-800">{formatDuration(remainingSeconds)}</div>
              </div>
            </div>
          )}
          {liveCounts && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-700 mb-4">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-gray-500">Files</div>
                <div className="font-semibold text-gray-800">
                  {formatCountWithTotal(liveCounts.files ?? null, liveCounts.filesTotal ?? null)}
                </div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-gray-500">Records</div>
                <div className="font-semibold text-gray-800">
                  {formatCountWithTotal(liveCounts.records ?? null, liveCounts.recordsTotal ?? null)}
                </div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-gray-500">Embeddings</div>
                <div className="font-semibold text-gray-800">
                  {formatCountWithTotal(liveCounts.embeddings ?? null, liveCounts.embeddingsTotal ?? null)}
                </div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-gray-500">Indexed</div>
                <div className="font-semibold text-gray-800">
                  {formatCountWithTotal(liveCounts.indexed ?? null, liveCounts.indexedTotal ?? null)}
                </div>
              </div>
            </div>
          )}
          {lastStageError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Error: {lastStageError}
                {lastStageCause ? ` — ${lastStageCause}` : ''}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            {stages.map((stage, idx) => (
              <React.Fragment key={stage.id}>
                <div className={`flex-1 text-center p-4 rounded-lg transition-colors ${
                  stage.status === 'completed' ? 'bg-green-50 border-2 border-green-200' :
                  stage.status === 'running' ? 'bg-blue-50 border-2 border-blue-300' :
                  stage.status === 'failed' ? 'bg-red-50 border-2 border-red-200' :
                  'bg-gray-50 border border-gray-200'
                }`}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <StageIcon status={stage.status} />
                    <span className="font-medium">{stage.name}</span>
                  </div>
                  <p className="text-xs text-gray-500">{stage.description}</p>
                </div>
                {idx < stages.length - 1 && (
                  <ArrowRight className="w-6 h-6 text-gray-300 mx-2 flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Schedule Configuration */}
      <div className="rounded-xl p-6 shadow-sm border-2" style={{ backgroundColor: envColors.bg, borderColor: envColors.primary }}>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setShowSchedule((prev) => !prev)}
            className="flex items-center gap-2 text-left"
          >
            <Clock className="w-5 h-5 text-purple-500" />
            <h3 className="font-semibold text-gray-900">Scheduled Updates</h3>
            {showSchedule ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => updateSchedule(!scheduleEnabled, scheduleInterval)}
              disabled={scheduleUpdating || !isDeployed}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                scheduleEnabled
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {scheduleUpdating ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : scheduleEnabled ? (
                <ToggleRight className="w-5 h-5" />
              ) : (
                <ToggleLeft className="w-5 h-5" />
              )}
              {scheduleEnabled ? 'Pause' : 'Enable'}
            </button>
            <button
              onClick={handleRunNow}
              disabled={!isDeployed || busy}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="w-4 h-4" />
              Run now
            </button>
          </div>
        </div>

        {showSchedule && (
          <>
            <p className="text-sm text-gray-500 mt-4 mb-4">
              Configure automatic data ingestion to keep your AEMO data up to date.
            </p>

            <div className="flex flex-wrap gap-2">
              {[
                { id: 'hourly', label: 'Hourly' },
                { id: '6-hourly', label: '6-Hourly' },
                { id: '12-hourly', label: '12-Hourly' },
                { id: 'daily', label: 'Daily' },
                { id: 'weekly', label: 'Weekly' },
                { id: 'monthly', label: 'Monthly' },
              ].map((option) => (
                <button
                  key={option.id}
                  onClick={() => {
                    setScheduleInterval(option.id);
                    if (scheduleEnabled) {
                      updateSchedule(true, option.id);
                    }
                  }}
                  disabled={!isDeployed}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                    scheduleInterval === option.id
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={updatedOnly}
                onChange={(e) => setUpdatedOnly(e.target.checked)}
                className="rounded"
              />
              Only new/updated records
            </label>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-gray-500">Schedule time</div>
                <div className="font-medium text-gray-800">{getScheduleTimeLabel(scheduleExpression)}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-gray-500">Last run</div>
                <div className="font-medium text-gray-800">{formatDateTime(scheduleLastRun)}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-gray-500">Next run</div>
                <div className="font-medium text-gray-800">{formatDateTime(scheduleNextRun)}</div>
              </div>
            </div>

            <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
              <div className="font-medium text-gray-800 mb-1">Scheduled parameters</div>
              <div>{describeParameters(scheduleParams).join(', ')}</div>
            </div>

            {!isDeployed && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Deploy infrastructure from <a href="/datastores/aemo" className="underline font-medium hover:text-yellow-800">Data Stores → AEMO Datastore</a> to enable scheduling.
              </div>
            )}
          </>
        )}
      </div>

      {/* Ingestion History with Delete Buttons */}
      <div className="rounded-xl shadow-sm border-2 overflow-hidden" style={{ backgroundColor: envColors.bg, borderColor: envColors.primary }}>
        <div
          className="flex items-center justify-between p-4 bg-gray-50 border-b cursor-pointer"
          onClick={() => setShowHistory(!showHistory)}
        >
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-900">Ingestion History</span>
            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
              {history.length}
            </span>
          </div>
          {showHistory ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>

        {showHistory && (
          <div className="p-4">
            {history.length === 0 ? (
              <p className="text-gray-500 text-sm">No ingestion runs found for this environment.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Select one or more runs to delete associated embeddings.
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={showFailedRuns}
                        onChange={(e) => setShowFailedRuns(e.target.checked)}
                        className="rounded"
                      />
                      Show failed runs
                    </label>
                    <button
                      onClick={() => handleDeleteRuns(selectedRuns)}
                      disabled={selectedRuns.length === 0 || deletingRuns.length > 0}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    >
                      {deletingRuns.length > 0 ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      Delete selected
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">
                          <input
                            type="checkbox"
                            checked={
                              history.filter(run => run.status !== 'RUNNING').length > 0 &&
                              history.filter(run => run.status !== 'RUNNING')
                                .every(run => selectedRuns.includes(run.executionId))
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRuns(history.filter(run => run.status !== 'RUNNING').map(run => run.executionId));
                              } else {
                                setSelectedRuns([]);
                              }
                            }}
                            className="rounded"
                          />
                        </th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Run time</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Parameters</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Records</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Embeddings</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Errors</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((run) => {
                        const isSelected = selectedRuns.includes(run.executionId);
                        const isDeleting = deletingRuns.includes(run.executionId);
                        return (
                          <tr key={run.executionId} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-3">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={run.status === 'RUNNING'}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedRuns(prev => [...prev, run.executionId]);
                                  } else {
                                    setSelectedRuns(prev => prev.filter(id => id !== run.executionId));
                                  }
                                }}
                                className="rounded"
                              />
                            </td>
                            <td className="py-3 px-3 text-gray-600">
                              {run.startDate ? new Date(run.startDate).toLocaleString() : '-'}
                            </td>
                            <td className="py-3 px-3">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  run.status === 'SUCCEEDED' ? 'bg-green-100 text-green-700' :
                                  run.status === 'RUNNING' ? 'bg-blue-100 text-blue-700' :
                                  run.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {run.status === 'SUCCEEDED' && <CheckCircle className="w-3 h-3" />}
                                  {run.status === 'RUNNING' && <Loader2 className="w-3 h-3 animate-spin" />}
                                  {run.status === 'FAILED' && <XCircle className="w-3 h-3" />}
                                  {run.status}
                                </span>
                                {deleteStatuses[run.executionId] && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    {deleteStatuses[run.executionId].status.replace('_', ' ').toUpperCase()}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-3 text-gray-600">
                              <div className="space-y-0.5 text-xs leading-tight">
                                {describeParameters(run.parameters).map(line => (
                                  <div key={line}>{line}</div>
                                ))}
                              </div>
                            </td>
                            <td className="py-3 px-3 text-gray-600">
                              {run.recordsProcessed ?? 0}
                            </td>
                            <td className="py-3 px-3 text-gray-600">
                              {run.embeddingsCreated ?? 0}
                            </td>
                            <td className="py-3 px-3 text-xs text-gray-600">
                              {run.error ? (
                                <div className="text-red-600">{run.error}</div>
                              ) : run.cause ? (
                                <div className="text-red-600">{run.cause}</div>
                              ) : run.status === 'FAILED' ? (
                                <div className="text-red-600">Failed (no details)</div>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="py-3 px-3 text-right">
                              <button
                                onClick={() => handleDeleteRun(run.executionId)}
                                disabled={isDeleting || run.status === 'RUNNING'}
                                className="inline-flex items-center gap-1 px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {isDeleting ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                                Delete Data
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Live Logs */}
      <div className="rounded-xl shadow-sm border-2 overflow-hidden" style={{ backgroundColor: envColors.bg, borderColor: envColors.primary }}>
        <div
          className="flex items-center justify-between p-4 bg-gray-50 border-b cursor-pointer"
          onClick={() => setShowLogs(!showLogs)}
        >
          <div className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-900">Live Logs</span>
            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
              {combinedLogs.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={logExecutionId}
              onChange={(e) => {
                setLogExecutionId(e.target.value);
                setRunLogs([]);
                logNextTokenRef.current = null;
              }}
              onClick={(e) => e.stopPropagation()}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1 bg-gray-100 text-gray-700"
            >
              {logRunOptions.length === 0 ? (
                <option value="">No runs</option>
              ) : (
                logRunOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))
              )}
            </select>
            <label className="flex items-center gap-1 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
                className="rounded"
                onClick={(e) => e.stopPropagation()}
              />
              Auto-scroll
            </label>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRefreshLogs();
              }}
              className="text-sm text-gray-600 border border-gray-200 rounded-lg px-2 py-1 bg-gray-100 hover:bg-gray-100"
            >
              Refresh
            </button>
            {showLogs ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>

        {showLogs && (
          <div className="bg-gray-900 text-gray-100 p-4 font-mono text-sm w-full h-[40vh] max-h-[40vh] overflow-y-auto overflow-x-hidden">
            {combinedLogs.length === 0 ? (
              <p className="text-gray-500">No logs yet. Start an ingestion to see logs.</p>
            ) : (
              combinedLogs.map((log) => (
                <div key={log.id || `${log.timestamp}-${log.level}-${log.message}`} className={`py-0.5 break-all ${
                  log.level === 'error' ? 'text-red-400' :
                  log.level === 'warn' ? 'text-yellow-400' :
                  log.level === 'success' ? 'text-green-400' :
                  'text-gray-300'
                }`}>
                  <span className="text-gray-500">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                  {log.stage && <span className="text-purple-400"> [{log.stage}]</span>}
                  <span> {log.message}</span>
                </div>
              ))
            )}
            <div ref={logsEndRef} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AemoIngestionPage;
